'use client';

import { c as _c } from "react/compiler-runtime";
import { useDotEnv } from '@repo/dot-env/client';
import { getGeolocationServiceUrl } from '@repo/dot-env/geolocation-service-url';
import { z } from '@repo/schema';
import { useQuery } from '@repo/tanstack/query';
import type { Coordinates } from './google-maps-input.ts';
import { fetchLookup, type LookupResult, toList } from './lookup-result.ts';
import { type LocationBias, UK_LOCATION_BIAS } from './use-nearby-bias.ts';
const PASTED_COORDINATES_BIAS_RADIUS = 100;
/** Text Search bias is soft, so pasted-link matches are kept only this close to the pin. */
const PASTED_MATCH_MAX_DISTANCE = 150;
const SEARCH_STALE_TIME = 5 * 60_000;
export type GoogleLocationResult = {
  formatted_address: string;
  name: string;
  place_id: string;
  place_name: string;
};
const textSearchSchema = z.object({
  results: z.object({
    formatted_address: z.string(),
    geometry: z.object({
      location: z.object({
        lat: z.number(),
        lng: z.number()
      })
    }),
    name: z.string(),
    place_id: z.string()
  }).array()
});
type TextSearchLocation = GoogleLocationResult & {
  location: Coordinates;
};
const exceedsTransportLimit = (label: string) => new TextEncoder().encode(encodeURIComponent(label)).byteLength > 255;
export const canSearchExactGoogleMapsLabel = (label: string) => !exceedsTransportLimit(label);
type PlaceText = Pick<GoogleLocationResult, 'formatted_address' | 'name'>;
const addressIncludesName = ({
  formatted_address,
  name
}: PlaceText) => !name || formatted_address === name || formatted_address.startsWith(`${name},`);
const getPlaceName = (place: PlaceText) => addressIncludesName(place) ? place.formatted_address : `${place.name}, ${place.formatted_address}`;

/**
 * The two lines of a result row: the name over the formatted address, or, when the address already
 * names the place, its first part over the rest.
 */
export const getPlaceLines = (place: PlaceText) => {
  if (!addressIncludesName(place)) return {
    address: place.formatted_address,
    title: place.name
  };
  const [title = '', ...rest] = place.formatted_address.split(', ');
  return {
    address: rest.join(', '),
    title
  };
};

/** Equirectangular approximation; accurate enough at the scale of a pasted pin. */
const getDistanceInMetres = (from: Coordinates, to: Coordinates) => {
  const toRadians = (degrees: number) => degrees * Math.PI / 180;
  const x = toRadians(to.lng - from.lng) * Math.cos(toRadians((from.lat + to.lat) / 2));
  const y = toRadians(to.lat - from.lat);
  return Math.hypot(x, y) * 6_371_000;
};
type SearchOptions = {
  bias?: LocationBias;
  serviceUrl?: string;
  signal?: AbortSignal;
};
const searchTextLocations = (query: string, {
  bias,
  serviceUrl = getGeolocationServiceUrl(),
  signal
}: SearchOptions = {}) => {
  const params = new URLSearchParams({
    query
  });
  if (bias) {
    params.set('location', `${bias.coordinates.lat},${bias.coordinates.lng}`);
    params.set('radius', String(bias.radius));
  }
  return fetchLookup(`${serviceUrl}/google/place/textsearch?${params}`, textSearchSchema, ({
    results
  }): null | TextSearchLocation[] => {
    const locations = new Map<string, TextSearchLocation>();
    for (const result of results) {
      if (!locations.has(result.place_id)) {
        locations.set(result.place_id, {
          formatted_address: result.formatted_address,
          location: result.geometry.location,
          name: result.name,
          place_id: result.place_id,
          place_name: getPlaceName(result)
        });
      }
    }
    return locations.size > 0 ? [...locations.values()] : null;
  }, signal);
};
export const searchLocation = async (query: string, options?: SearchOptions): Promise<LookupResult<GoogleLocationResult[]>> => {
  const result = await searchTextLocations(query, options);
  return result.status === 'found' ? {
    status: 'found',
    value: result.value.map(({
      location: _location,
      ...place
    }) => place)
  } : result;
};
export const useSearchLocation = t0 => {
  const $ = _c(14);
  const {
    bias: t1,
    enabled: t2,
    query: t3
  } = t0;
  const bias = t1 === undefined ? UK_LOCATION_BIAS : t1;
  const enabled = t2 === undefined ? true : t2;
  const query = t3 === undefined ? "" : t3;
  const {
    GEOLOCATION_SERVICE_URL
  } = useDotEnv();
  const t4 = enabled && query.length > 2;
  let t5;
  if ($[0] !== GEOLOCATION_SERVICE_URL || $[1] !== bias || $[2] !== query) {
    t5 = async t6 => {
      const {
        signal
      } = t6;
      return toList(await searchLocation(query, {
        bias,
        serviceUrl: GEOLOCATION_SERVICE_URL,
        signal
      }));
    };
    $[0] = GEOLOCATION_SERVICE_URL;
    $[1] = bias;
    $[2] = query;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== GEOLOCATION_SERVICE_URL || $[5] !== bias.coordinates.lat || $[6] !== bias.coordinates.lng || $[7] !== bias.radius || $[8] !== query) {
    t6 = ["location-search", query, GEOLOCATION_SERVICE_URL, bias.coordinates.lat, bias.coordinates.lng, bias.radius];
    $[4] = GEOLOCATION_SERVICE_URL;
    $[5] = bias.coordinates.lat;
    $[6] = bias.coordinates.lng;
    $[7] = bias.radius;
    $[8] = query;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = {
      enabled: t4,
      queryFn: t5,
      queryKey: t6,
      staleTime: SEARCH_STALE_TIME
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return useQuery(t7);
};
const normalizeLabel = (value: string) => value.normalize('NFKD').replace(/\p{Mark}/gu, '').trim().toLocaleLowerCase();
export const searchExactGoogleLocations = async ({
  coordinates,
  label,
  serviceUrl = getGeolocationServiceUrl(),
  signal
}: {
  coordinates?: Coordinates;
  label: string;
  serviceUrl?: string;
  signal?: AbortSignal;
}): Promise<LookupResult<GoogleLocationResult[]>> => {
  if (exceedsTransportLimit(label)) return {
    status: 'empty'
  };
  const result = await searchTextLocations(label, {
    bias: coordinates ? {
      coordinates,
      radius: PASTED_COORDINATES_BIAS_RADIUS
    } : undefined,
    serviceUrl,
    signal
  });
  if (result.status !== 'found') return result;
  const normalizedLabel = normalizeLabel(label);
  const matches = result.value.filter(({
    location,
    place_name
  }) => {
    const normalizedPlaceName = normalizeLabel(place_name);
    const matchesLabel = normalizedPlaceName === normalizedLabel || normalizedPlaceName.startsWith(`${normalizedLabel},`);
    return matchesLabel && (!coordinates || getDistanceInMetres(coordinates, location) <= PASTED_MATCH_MAX_DISTANCE);
  }).map(({
    location: _location,
    ...place
  }) => place);
  return matches.length > 0 ? {
    status: 'found',
    value: matches
  } : {
    status: 'empty'
  };
};
export const useExactGoogleLocationSearch = t0 => {
  const $ = _c(15);
  const {
    coordinates,
    enabled: t1,
    label
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    GEOLOCATION_SERVICE_URL
  } = useDotEnv();
  let t2;
  if ($[0] !== label) {
    t2 = !!label && canSearchExactGoogleMapsLabel(label);
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const canSearch = t2;
  const t3 = enabled && canSearch;
  let t4;
  if ($[2] !== GEOLOCATION_SERVICE_URL || $[3] !== coordinates || $[4] !== label) {
    t4 = async t5 => {
      const {
        signal
      } = t5;
      return toList(await searchExactGoogleLocations({
        coordinates,
        label: label ?? "",
        serviceUrl: GEOLOCATION_SERVICE_URL,
        signal
      }));
    };
    $[2] = GEOLOCATION_SERVICE_URL;
    $[3] = coordinates;
    $[4] = label;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = coordinates?.lat ?? null;
  const t6 = coordinates?.lng ?? null;
  let t7;
  if ($[6] !== GEOLOCATION_SERVICE_URL || $[7] !== label || $[8] !== t5 || $[9] !== t6) {
    t7 = ["google-maps-exact-label-search", GEOLOCATION_SERVICE_URL, label, t5, t6];
    $[6] = GEOLOCATION_SERVICE_URL;
    $[7] = label;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t7) {
    t8 = {
      enabled: t3,
      queryFn: t4,
      queryKey: t7,
      staleTime: SEARCH_STALE_TIME
    };
    $[11] = t3;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return useQuery(t8);
};